import { Toogle, Input } from 'alurkerja-ui'
import { useUiFromSpecContext } from '../../../EditorContextProvider'
import { stringToTitleCase } from '@/utils/functions/stringToTitleCase'
import React, { useCallback, useMemo, useState, useEffect } from 'react'
import { UseFormReturn } from 'react-hook-form'
import {
  validateRequiredIfExpression,
  getValidationMessage,
  getRequiredIfExamples,
} from '../../../FormRenderer/utils/validateRequiredIfExpression'

export default function ValidationEditElement({
  form,
  currentElementState,
  setCurrentElementState,
}: {
  form: UseFormReturn<any>
  currentElementState: any
  setCurrentElementState: React.Dispatch<any>
}) {
  const { setDisableSaveEdit } = useUiFromSpecContext()
  const [validationResult, setValidationResult] = useState<{
    isValid: boolean
    errors: string[]
    warnings?: string[]
  } | null>(null)
  const [showExamples, setShowExamples] = useState(false)

  // Get the value from currentElementState, properly memoized
  const currentValue = useMemo(() => !!currentElementState?.['constraints']?.['required'], [currentElementState])
  const currentRequiredIfExpression = useMemo(() => currentElementState?.['constraints']?.['required_if']?.expression || '', [currentElementState])

  // Check if field is not required (to show required_if option)
  const isNotRequired = useMemo(() => !currentValue, [currentValue])

  // Validate required_if expression whenever it changes
  useEffect(() => {
    if (currentRequiredIfExpression.trim() && isNotRequired) {
      const result = validateRequiredIfExpression(currentRequiredIfExpression)
      setValidationResult(result)
    } else {
      setValidationResult(null)
    }
  }, [currentRequiredIfExpression, isNotRequired])

  useEffect(() => {
    setDisableSaveEdit?.((validationResult && !validationResult.isValid) ?? false)
  }, [validationResult, setDisableSaveEdit])

  // Memoize the onChange handler to prevent recreating it on each render
  const handleChange = useCallback(
    (value: string | number | boolean | undefined) => {
      form.clearErrors
      // Use a functional update to ensure we're working with the latest state
      setCurrentElementState((prevState: any) => {
        // Only update if the value is actually different to prevent unnecessary renders
        if (!!prevState?.['constraints']?.['required'] === !!value) {
          return prevState
        }

        // Create a new constraints object based on existing constraints
        const newConstraints = { ...(prevState?.['constraints'] || {}) }

        if (value) {
          // If value is true, set required constraint and remove required_if
          newConstraints.required = true
          delete newConstraints.required_if
        } else {
          // If value is false, remove required constraint
          delete newConstraints.required
        }

        return {
          ...prevState,
          ['constraints']: newConstraints,
        }
      })
    },
    [setCurrentElementState, form.clearErrors]
  )

  const handleRequiredIfChange = useCallback(
    (expression: string) => {
      setCurrentElementState((prevState: any) => {
        const newConstraints = { ...(prevState?.['constraints'] || {}) }

        if (expression.trim()) {
          newConstraints.required_if = {
            expression: expression,
          }
        } else {
          delete newConstraints.required_if
        }

        return {
          ...prevState,
          ['constraints']: newConstraints,
        }
      })
    },
    [setCurrentElementState]
  )

  if (currentElementState?.['form_field_type'] === 'INPUT_SWITCH') {
    // If the current element is not an input, return null
    return null
  }

  return React.useMemo(
    () => (
      <div className="space-y-4">
        {/* Required Toggle */}
        <div className="p-3 py-2 flex gap-2 rounded-sm">
          <Toogle id="required" defaultValue={currentValue} onChange={handleChange} />
          <label htmlFor={`required`} className="select-none">
            {stringToTitleCase('Required')}
          </label>
        </div>

        {/* Required If Section - Only show when field is not required */}
        {isNotRequired && (
          <div className="p-3 py-2 rounded-sm">
            <div className="flex justify-between items-center mb-2">
              <label className="font-semibold">Required If</label>
              <button
                type="button"
                onClick={() => setShowExamples(!showExamples)}
                className="text-xs text-blue-600 hover:text-blue-800 underline"
              >
                {showExamples ? 'Hide Examples' : 'Show Examples'}
              </button>
            </div>

            <Input
              className={`w-full ${
                validationResult
                  ? validationResult.isValid
                    ? 'border-green-300 focus:border-green-500'
                    : 'border-red-300 focus:border-red-500'
                  : ''
              }`}
              size="sm"
              rounded="lg"
              type="text"
              placeholder="e.g., user_type == 'Premium' && is_active == true"
              value={currentRequiredIfExpression}
              onChange={(e) => handleRequiredIfChange(e.target.value)}
            />

            {/* Validation Messages */}
            {validationResult && (
              <div className="mt-2">
                {validationResult.isValid ? (
                  <div className="flex items-center text-green-600 text-xs">
                    <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Valid expression
                  </div>
                ) : (
                  <div className="text-red-600 text-xs">
                    <div className="flex items-start">
                      <svg className="w-3 h-3 mr-1 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <div>{getValidationMessage(validationResult)}</div>
                    </div>
                  </div>
                )}

                {/* Warnings */}
                {validationResult.warnings && validationResult.warnings.length > 0 && (
                  <div className="text-yellow-600 text-xs mt-1">
                    <div className="flex items-start">
                      <svg className="w-3 h-3 mr-1 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <div>{validationResult.warnings.join('; ')}</div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Examples */}
            {showExamples && (
              <div className="mt-3 p-2 bg-gray-50 rounded-lg text-xs">
                <div className="font-medium text-gray-700 mb-2">Valid required_if expression examples:</div>
                <div className="space-y-1">
                  {getRequiredIfExamples().map((example, index) => (
                    <div key={index} className="text-gray-600 font-mono">
                      {example}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    ),
    [currentValue, handleChange, isNotRequired, currentRequiredIfExpression, handleRequiredIfChange, validationResult, showExamples]
  )
}
